import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldAlert, Search, X, CheckCircle2, XCircle, Lock, History, FileText, Send, Eye,
} from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { notifyToast } from '../../lib/toastStore';
import { promptDialog } from '../../lib/confirmStore';
import type { OrgSpecialRequest } from '../../types';
import { fetchOrgSpecialRequestTemplates } from '../../lib/orgSpecialRequestTemplates';

/**
 * AdminOrgSpecialRequestsManager — cross-organization monitoring and
 * review for Organization Special Requests (program 2, T3). Delivery,
 * submission, approval, rejection, and completion are DISTINCT
 * states here: a Submitted request is never shown as approved.
 * Per-organization tracking stays independent — every row is one
 * organization's own request.
 */

const STATUS_STYLES: Record<string, string> = {
  Draft: 'bg-slate-100 text-slate-600',
  Pending: 'bg-amber-100 text-amber-700',
  Viewed: 'bg-sky-100 text-sky-700',
  Submitted: 'bg-indigo-100 text-indigo-700',
  Approved: 'bg-emerald-100 text-emerald-700',
  Completed: 'bg-emerald-100 text-emerald-700',
  Rejected: 'bg-red-100 text-red-700',
  Cancelled: 'bg-red-100 text-red-600',
  Overdue: 'bg-red-100 text-red-700',
  Expired: 'bg-red-100 text-red-700',
};

const STATUSES = ['Draft', 'Pending', 'Viewed', 'Submitted', 'Approved', 'Rejected', 'Completed', 'Cancelled', 'Overdue', 'Expired'];
const TERMINAL = ['Completed', 'Cancelled', 'Approved'];

const BUCKETS: { id: string; label: string }[] = [
  { id: '', label: 'All' },
  { id: 'awaitingApproval', label: 'Awaiting approval' },
  { id: 'notSubmitted', label: 'Not submitted' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'overdue', label: 'Overdue' },
];

function fmtDate(v?: string | null) {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}
function fmtDateTime(v?: string | null) {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

type Row = OrgSpecialRequest & {
  outstanding?: boolean;
  submissionCount?: number;
  latestSubmissionAt?: string | null;
  bucket?: string | null;
};

export default function AdminOrgSpecialRequestsManager({
  canEdit = true,
  onOpenTemplates,
}: {
  canEdit?: boolean;
  onOpenTemplates?: () => void;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [orgType, setOrgType] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [bucket, setBucket] = useState('');
  const [submittedFrom, setSubmittedFrom] = useState('');
  const [submittedTo, setSubmittedTo] = useState('');
  const [dueBefore, setDueBefore] = useState('');
  const [templates, setTemplates] = useState<{ templateId: string; templateName: string }[]>([]);
  const [selected, setSelected] = useState<Row | null>(null);

  useEffect(() => {
    fetchOrgSpecialRequestTemplates()
      .then((ts) => setTemplates(ts.map((t) => ({ templateId: t.templateId, templateName: t.templateName }))))
      .catch(() => setTemplates([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (orgType) params.set('orgType', orgType);
      if (search.trim()) params.set('search', search.trim());
      if (status) params.set('status', status);
      if (templateId) params.set('templateId', templateId);
      if (bucket) params.set('bucket', bucket);
      if (submittedFrom) params.set('submittedFrom', new Date(submittedFrom).toISOString());
      if (submittedTo) params.set('submittedTo', new Date(submittedTo + 'T23:59:59').toISOString());
      if (dueBefore) params.set('dueBefore', new Date(dueBefore + 'T23:59:59').toISOString());
      const qs = params.toString();
      const data = await safeFetch(`/api/admin/org-special-requests${qs ? `?${qs}` : ''}`);
      setRows(data.requests || []);
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to load special requests');
    } finally {
      setLoading(false);
    }
  }, [orgType, search, status, templateId, bucket, submittedFrom, submittedTo, dueBefore]);

  useEffect(() => { load(); }, [load]);

  const counts = {
    awaiting: rows.filter((r) => r.bucket === 'awaitingApproval').length,
    rejected: rows.filter((r) => r.bucket === 'rejected').length,
    overdue: rows.filter((r) => r.bucket === 'overdue').length,
    outstanding: rows.filter((r) => r.outstanding).length,
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <ShieldAlert size={20} className="text-indigo-600" /> Organization Special Requests
        </h2>
        {onOpenTemplates && (
          <button onClick={onOpenTemplates} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-brand-primary text-white text-sm font-medium hover:bg-brand-primary-dark">
            <Send size={15} /> Send from a template
          </button>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Every organization&apos;s requests, tracked independently. Submitted is not approved: approval-required
        submissions wait here until you approve or reject them.
        {counts.awaiting > 0 && <span className="text-amber-600 font-medium"> {counts.awaiting} awaiting approval.</span>}
        {counts.outstanding > 0 && <span className="text-red-600 font-medium"> {counts.outstanding} blocking outstanding.</span>}
      </p>

      <div className="flex flex-wrap gap-2 mb-3">
        {BUCKETS.map((b) => (
          <button
            key={b.id}
            onClick={() => setBucket(b.id)}
            className={`px-3 py-1.5 rounded-lg text-sm border cursor-pointer ${bucket === b.id ? 'bg-brand-primary text-white border-brand-primary' : 'border-slate-200 text-slate-600 bg-white'}`}
          >
            {b.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <select value={orgType} onChange={(e) => setOrgType(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
          <option value="">All org types</option>
          <option value="referrer">Referrers</option>
          <option value="sponsor">Sponsors</option>
          <option value="employer">Employers</option>
        </select>
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search organization or request…" className="pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm w-64" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
          <option value="">All templates</option>
          {templates.map((t) => <option key={t.templateId} value={t.templateId}>{t.templateName}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-slate-500">
          Submitted from
          <input type="date" value={submittedFrom} onChange={(e) => setSubmittedFrom(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm" />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-500">
          to
          <input type="date" value={submittedTo} onChange={(e) => setSubmittedTo(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm" />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-slate-500">
          Due before
          <input type="date" value={dueBefore} onChange={(e) => setDueBefore(e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm" />
        </label>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 py-8 text-center">Loading requests…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-8 text-center">No requests match these filters.</p>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[880px]">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                <th className="px-4 py-2.5 font-semibold">Organization</th>
                <th className="px-4 py-2.5 font-semibold">Request</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5 font-semibold">Issued</th>
                <th className="px-4 py-2.5 font-semibold">Due</th>
                <th className="px-4 py-2.5 font-semibold">Submitted</th>
                <th className="px-4 py-2.5 font-semibold">Versions</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {rows.map((r) => (
                <tr key={r.requestId} className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(r)}>
                  <td className="px-4 py-2.5">
                    <span className="font-medium text-slate-700">{r.orgName}</span>
                    <span className="text-slate-400 capitalize"> · {r.orgType}</span>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">
                    {r.documentName}
                    {r.templateName && <span className="text-slate-400"> · {r.templateName}</span>}
                    {r.requestType === 'Verification' && <span className="ml-1 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[10px] font-bold">Verification</span>}
                    {r.requestType === 'Form' && <span className="ml-1 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[10px] font-bold">Form</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${STATUS_STYLES[r.status] || 'bg-slate-100 text-slate-500'}`}>{r.status}</span>
                    {r.outstanding && <span className="ml-1.5 text-[11px] font-bold text-red-600">Blocking</span>}
                    {r.bucket === 'awaitingApproval' && (
                      <span className="ml-1.5 inline-flex items-center gap-0.5 text-[11px] font-bold text-amber-600"><Lock size={11} /> Awaiting approval</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500">{fmtDate(r.sentAt || r.createdAt)}</td>
                  <td className="px-4 py-2.5 text-slate-500">{fmtDate(r.dueDate)}</td>
                  <td className="px-4 py-2.5 text-slate-500">{fmtDate(r.latestSubmissionAt || r.submittedAt)}</td>
                  <td className="px-4 py-2.5 text-slate-500">{r.submissionCount || 0}</td>
                  <td className="px-4 py-2.5 text-indigo-600"><Eye size={16} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <RequestDetailDrawer
          request={selected}
          canEdit={canEdit}
          onClose={() => setSelected(null)}
          onChanged={async () => { setSelected(null); await load(); }}
        />
      )}
    </div>
  );
}

function RequestDetailDrawer({ request: r, canEdit, onClose, onChanged }: {
  request: Row;
  canEdit: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState('');
  const versions = Array.isArray(r.submissions) ? r.submissions : [];
  const requiresApproval = r.requiresApproval !== false;
  const isVerification = r.requestType === 'Verification';

  const act = async (action: string, body?: any) => {
    setBusy(action);
    try {
      await safeFetch(`/api/admin/org-special-requests/${r.requestId}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      notifyToast('success', `Request ${action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : action + 'd'}`);
      onChanged();
    } catch (err: any) {
      notifyToast('error', err.message || `Failed to ${action} the request`);
    } finally {
      setBusy('');
    }
  };

  const reasoned = async (action: 'override' | 'cancel') => {
    const reason = await promptDialog(
      action === 'override'
        ? `Override "${r.documentName}" and mark it Completed without a submission? A reason is required and will be recorded.`
        : `Cancel "${r.documentName}"? A reason is required and will be recorded.`,
    );
    if (reason === null) return;
    if (reason.trim().length < 3) {
      notifyToast('error', 'A reason of at least a few characters is required');
      return;
    }
    act(action, { reason: reason.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-bold text-slate-800">{r.documentName}</h3>
          <button aria-label="Close" title="Close" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          {r.orgName} · <span className="capitalize">{r.orgType}</span> · to {r.recipientEmail || '—'}
          {r.templateName ? ` · from template “${r.templateName}”` : ' · ad hoc'}
        </p>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mb-4">
          <div><dt className="text-[11px] font-bold text-slate-400 uppercase">Status</dt><dd>
            <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${STATUS_STYLES[r.status] || 'bg-slate-100 text-slate-500'}`}>{r.status}</span>
            {r.outstanding && <span className="ml-1.5 text-red-600 text-xs font-bold">Blocks workspace access</span>}
          </dd></div>
          <div><dt className="text-[11px] font-bold text-slate-400 uppercase">Issued</dt><dd className="text-slate-600">{fmtDateTime(r.sentAt || r.createdAt)}</dd></div>
          <div><dt className="text-[11px] font-bold text-slate-400 uppercase">Deadline</dt><dd className="text-slate-600">{fmtDate(r.dueDate)}</dd></div>
          <div><dt className="text-[11px] font-bold text-slate-400 uppercase">Approval required</dt><dd className="text-slate-600">{requiresApproval ? 'Yes' : 'No'}</dd></div>
          {r.description && <div className="col-span-2"><dt className="text-[11px] font-bold text-slate-400 uppercase">Instructions</dt><dd className="text-slate-600 whitespace-pre-wrap">{r.description}</dd></div>}
          {r.reviewedAt && (
            <div className="col-span-2">
              <dt className="text-[11px] font-bold text-slate-400 uppercase">Review</dt>
              <dd className="text-slate-600">
                {r.reviewDecision} by {r.reviewedByName || r.reviewedBy || 'an administrator'} · {fmtDateTime(r.reviewedAt)}
                {r.rejectionReason ? <span className="block text-red-600 mt-0.5">Reason: {r.rejectionReason}</span> : null}
              </dd>
            </div>
          )}
          {r.overrideReason && <div className="col-span-2"><dt className="text-[11px] font-bold text-slate-400 uppercase">Override reason</dt><dd className="text-slate-600">{r.overrideReason}</dd></div>}
          {r.cancelReason && <div className="col-span-2"><dt className="text-[11px] font-bold text-slate-400 uppercase">Cancel reason</dt><dd className="text-slate-600">{r.cancelReason}</dd></div>}
        </dl>

        <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1">
          <History size={12} /> Submissions
        </h4>
        {versions.length === 0 ? (
          <p className="text-sm text-slate-400 mb-4">{r.submittedAt ? `Submitted ${fmtDateTime(r.submittedAt)} (before versioning).` : 'No submission yet.'}</p>
        ) : (
          <div className="space-y-2 mb-4">
            {[...versions].reverse().map((v) => (
              <div key={v.version} className="border border-slate-100 rounded-lg p-3 text-sm">
                <p className="text-xs font-bold text-slate-500">
                  Version {v.version} · {fmtDateTime(v.submittedAt)} · by {v.submittedBy === 'public_token' ? 'Emailed link' : v.submittedBy}
                  {v.version === versions.length ? ' · Latest — the active version for review' : ''}
                </p>
                {v.responseValue && !v.formResponses && <p className="text-slate-600 whitespace-pre-wrap mt-1">{v.responseValue}</p>}
                {(v.selectedValues || []).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(v.selectedValues || []).map((val, i) => (
                      <span key={i} className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[11px] font-semibold">{val}</span>
                    ))}
                  </div>
                )}
                {v.formResponses && (
                  <dl className="mt-1 space-y-0.5">
                    {Object.entries(v.formResponses).map(([k, val]) => {
                      // Form answers are keyed by field id — show the
                      // field's label, the way applicant review does.
                      const label = (Array.isArray(r.formFields) ? r.formFields : []).find((f: any) => f && f.id === k)?.label || k;
                      return (
                        <div key={k} className="text-xs text-slate-600"><span className="font-semibold">{label}:</span> {Array.isArray(val) ? val.join(', ') : String(val ?? '')}</div>
                      );
                    })}
                  </dl>
                )}
                {(v.responseFiles || []).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-2">
                    {(v.responseFiles || []).map((f, i) => (
                      <a key={i} href={f.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-600 underline">
                        <FileText size={12} /> {f.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {canEdit && !isVerification && (
          <div className="border-t border-slate-100 pt-4 space-y-3">
            {r.status === 'Submitted' && (
              <>
                <div className="flex flex-wrap gap-2">
                  {requiresApproval ? (
                    <button onClick={() => act('approve')} disabled={!!busy} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-success text-white text-sm font-medium hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:opacity-50">
                      <CheckCircle2 size={15} /> Approve submission
                    </button>
                  ) : (
                    <button onClick={() => act('complete')} disabled={!!busy} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-success text-white text-sm font-medium hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:opacity-50">
                      <CheckCircle2 size={15} /> Mark completed (reviewed)
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Rejection reason (required — the organization sees this)"
                    className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                  />
                  <button
                    onClick={() => rejectReason.trim().length >= 3 ? act('reject', { reason: rejectReason.trim() }) : notifyToast('error', 'A rejection reason is required')}
                    disabled={!!busy}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                  >
                    <XCircle size={15} /> Reject
                  </button>
                </div>
              </>
            )}
            {!TERMINAL.includes(r.status) && (
              <div className="flex gap-2">
                <button onClick={() => reasoned('override')} disabled={!!busy} className="px-3 py-1.5 rounded-lg border border-amber-200 text-amber-700 text-xs font-bold hover:bg-amber-50">Override</button>
                <button onClick={() => reasoned('cancel')} disabled={!!busy} className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs font-bold hover:bg-red-50">Cancel request</button>
              </div>
            )}
          </div>
        )}
        {isVerification && (
          <p className="text-sm text-violet-600 bg-violet-50 border border-violet-100 rounded-lg p-3">
            Verification request — it completes only when Didit confirms the verification. It cannot be approved, rejected, overridden, or cancelled here.
          </p>
        )}
      </div>
    </div>
  );
}
